import React from 'react';
import { FaWhatsapp, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaInstagram, FaYoutube, FaGlobe, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ContactSupport = ({ contentData }) => {
  const contactChannels = [
    {
      icon: <FaWhatsapp className="text-xl" />,
      title: contentData?.contact_card1_title,
      details: contentData?.contact_card1_value,
      description: contentData?.contact_card1_info,
      color: "from-green-500 to-green-600",
      gradient: "bg-gradient-to-r from-gray-800 to-green-900/20",
      link: "https://wa.me/919319745997"
    },
    {
      icon: <FaPhoneAlt className="text-xl" />,
      title: contentData?.contact_card2_title,
      details: contentData?.contact_card2_value,
      description: contentData?.contact_card2_info,
      color: "from-blue-500 to-blue-600",
      gradient: "bg-gradient-to-r from-gray-800 to-blue-900/20",
      link: "tel:9319745997"
    },
    {
      icon: <FaEnvelope className="text-xl" />,
      title: contentData?.contact_card3_title,
      details: contentData?.contact_card3_value,
      description: contentData?.contact_card3_info,
      color: "from-red-500 to-red-600",
      gradient: "bg-gradient-to-r from-gray-800 to-red-900/20",
      link: "mailto:support@marketmaestroo.com"
    }
  ];

  const socialLinks = [
    {
      icon: <FaInstagram />,
      platform: contentData?.insta_id1,
      followers: contentData?.insta_id1_info,
      color: "from-pink-500 to-red-500",
      link: contentData?.insta_id1_link
    },
    {
      icon: <FaInstagram />,
      platform: contentData?.insta_id2,
      followers: contentData?.insta_id2_info,
      color: "from-purple-500 to-pink-500",
      link: contentData?.insta_id2_link
    },
    {
      icon: <FaYoutube />,
      platform: contentData?.youtube_title,
      followers: contentData?.youtube_info,
      color: "from-red-500 to-red-600",
      link: contentData?.youtube_link
    },
    {
      icon: <FaGlobe />,
      platform: contentData?.main_website_title,
      details: contentData?.main_website_info,
      color: "from-blue-500 to-cyan-500",
      link: contentData?.main_website_link
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" id='contact'>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            We're here to help you with any questions or support you need
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Support Channels */}
          {contactChannels.map((channel, index) => (
            <motion.a
              key={index}
              href={channel.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
              className={`${channel.gradient} p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700 backdrop-blur-sm`}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-14 h-14 bg-gradient-to-r ${channel.color} rounded-xl flex items-center justify-center`}>
                  <div className="text-white">
                    {channel.icon}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-white">{channel.title}</h3>
                  <p className="text-sm text-gray-300">{channel.description}</p>
                </div>
              </div>
              <p className="text-lg font-bold text-gray-100">{channel.details}</p>
              <p className="text-sm text-gray-400 mt-2">⏰ Response Time: Within 24 Hours</p>
            </motion.a>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Office Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-gray-800/60 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-gray-700"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <FaMapMarkerAlt className="text-white text-xl" />
              </div>
              <h3 className="text-xl font-bold text-white">Office Address</h3>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-800/30">
                <p className="font-bold text-white mb-1">Registered Office:</p>
                <p className="text-gray-300">
                  Market Maestroo Private Limited<br />
                  Shop No 36, First Floor, Commercial Complex<br />
                  Nehru Nagar East, Durg, Chhattisgarh 490020
                </p>
              </div>

              <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-800/30">
                <div className="flex items-center gap-2 mb-2">
                  <FaClock className="text-yellow-400" />
                  <span className="font-bold text-white">Office Hours:</span>
                </div>
                <div className="text-gray-300">
                  <p>Monday to Saturday: 10:00 AM - 6:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Social Media & Links */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gray-800/60 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-gray-700"
          >
            <h3 className="text-xl font-bold text-white mb-6">Connect With Us</h3>
            
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`bg-gradient-to-r ${social.color} text-white p-4 rounded-xl hover:shadow-lg hover:shadow-${social.color.split('-')[1]}-500/20 transition-all duration-300 transform hover:-translate-y-1 border border-gray-700`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-xl">
                      {social.icon}
                    </div>
                    <div className="text-sm">
                      <p className="font-bold">{social.platform}</p>
                      <p className="text-white/80 text-xs">{social.followers || social.details}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            {/* New Subscriptions */}
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/20 p-4 rounded-xl border border-green-800/30">
              <h4 className="font-bold text-white mb-2">For New Subscriptions:</h4>
              <div className="space-y-2">
                <a 
                  href={contentData?.main_website_link}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <FaGlobe />
                  <span>Visit: {contentData?.main_website_link}</span>
                </a>
                <a 
                  href={`tel:${contentData?.phone}`} 
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <FaPhoneAlt />
                  <span>Call: {contentData?.phone}</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSupport;